import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import moment from 'moment';
import './BrandOnboardingDrawer.scss';

import Tooltip from '../General/Tooltip.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faCopy } from '@fortawesome/pro-regular-svg-icons';

import BrandOnboardingOverlay from './BrandOnboardingOverlay';
import BrandOnboardingEditor from './BrandOnboardingEditor';
import BrandOnboardingProgress from './BrandOnboardingProgress';
import { StatusChip, CodeChip } from './BrandOnboardingChips';
import { copyToClipboard, getOnboardingStatusChip } from './brandOnboardingHelpers';

const BrandOnboardingDrawer = props => {
  const { registrationCode, fields, isFieldEditable, onFieldChange, collapsedSectionTitles, onToggleSectionCollapsed } = props;
  const { recipientNamesById, adminUsers, isLoading, hasUnsavedChanges, onUpdate, onRequestClose } = props;

  const statusChip = getOnboardingStatusChip(registrationCode);
  const recipientName = registrationCode.NotificationsUser_id && recipientNamesById.get(registrationCode.NotificationsUser_id);
  const recipientHasSlackChannel = adminUsers.some(user => user.id === registrationCode.NotificationsUser_id);

  const stageNote = !registrationCode.isUsed
    ? 'This code has not been redeemed yet — everything below stays editable until the brand registers with it.'
    : statusChip.className === 'complete'
    ? 'This brand is live. Its details, billing, network and rates have all been applied — only the notifications recipient can still be changed.'
    : 'Brand details and billing were applied when the brand registered and are locked. Network & rates stay editable until the subscription starts.';

  const header = (
    <div className='brand-onboarding-editor-header'>
      <div>
        <div className='brand-onboarding-editor-title'>{registrationCode.brand?.name || registrationCode.name}</div>
        <div className='brand-onboarding-editor-meta'>
          <StatusChip registrationCode={registrationCode} />
          <CodeChip code={registrationCode.code} onCopy={() => copyToClipboard(registrationCode.code)} />
          <span className='brand-onboarding-editor-meta-age' title={moment(registrationCode.createdAt).format('MMM D, YYYY')}>
            Created {moment(registrationCode.createdAt).fromNow()}
          </span>
        </div>
      </div>

      {!!recipientName && (
        <Tooltip
          message={
            recipientHasSlackChannel
              ? `${recipientName} receives this brand's onboarding updates in Slack`
              : `${recipientName} has no personal Slack channel — updates go to #brand-onboarding-updates only`
          }
        >
          <div className='brand-onboarding-notify-pill'>
            <FontAwesomeIcon icon={faBell} />
            {recipientName}
          </div>
        </Tooltip>
      )}
    </div>
  );

  const actions = (
    <button
      className={cn('brand-onboarding-result-button save', { disabled: isLoading || !hasUnsavedChanges })}
      disabled={isLoading || !hasUnsavedChanges}
      onClick={onUpdate}
    >
      {isLoading ? 'Saving...' : 'Update Code'}
    </button>
  );

  return (
    <BrandOnboardingOverlay onRequestClose={onRequestClose}>
      <div className='brand-onboarding-drawer-outer-container' onClick={e => e.stopPropagation()}>
        <div className='brand-onboarding-drawer-inner-container'>
          <BrandOnboardingEditor
            fields={fields}
            isFieldEditable={isFieldEditable}
            onFieldChange={onFieldChange}
            collapsedSectionTitles={collapsedSectionTitles}
            onToggleSectionCollapsed={onToggleSectionCollapsed}
            recipientNamesById={recipientNamesById}
            header={header}
            actions={actions}
          >
            <div className='brand-onboarding-stage-note'>{stageNote}</div>

            {!registrationCode.isUsed && (
              <div className='brand-onboarding-code-hero'>
                <div className='brand-onboarding-code-hero-code'>{registrationCode.code}</div>
                <button className='brand-onboarding-code-hero-copy' onClick={() => copyToClipboard(registrationCode.code)}>
                  <FontAwesomeIcon icon={faCopy} />
                  Copy
                </button>
              </div>
            )}

            <BrandOnboardingProgress brand={registrationCode.brand} />
          </BrandOnboardingEditor>
        </div>
      </div>
    </BrandOnboardingOverlay>
  );
};

BrandOnboardingDrawer.propTypes = {
  registrationCode: PropTypes.object.isRequired,
  fields: PropTypes.array.isRequired,
  isFieldEditable: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  collapsedSectionTitles: PropTypes.object.isRequired,
  onToggleSectionCollapsed: PropTypes.func.isRequired,
  recipientNamesById: PropTypes.object.isRequired,
  adminUsers: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  hasUnsavedChanges: PropTypes.bool.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onRequestClose: PropTypes.func.isRequired
};

export default BrandOnboardingDrawer;
